// Core packages
import { Injectable } from "@angular/core";
import { CanDeactivate } from "@angular/router";

// Third party packages
import { Observable } from "rxjs";

// Custom packages

/**
 * Contract a component must honour to be guarded against losing pending changes.
 *
 * @since 3.0.0
 */
export interface CanComponentDeactivate {
  /**
   * Return true to let the navigation through, false to block it, or an Observable
   * resolving to either when the answer comes from a dialog.
   */
  canDeactivate(): boolean | Observable<boolean>;
}

/**
 * Generic "unsaved changes" route guard: it holds no UI of its own, it just asks the
 * component. Which dialog to show, and whether to show one at all, is a decision of
 * the page that owns the pending changes.
 *
 *   { path: 'x', component: XComponent, canDeactivate: [UnsavedChangesGuard] }
 *
 * @since 3.0.0
 */
@Injectable({
  providedIn: "root",
})
export class UnsavedChangesGuard
  implements CanDeactivate<CanComponentDeactivate>
{
  /**
   * Delegate the decision to the component. A component that does not implement the
   * contract never blocks a navigation.
   *
   * @since 3.0.0
   */
  canDeactivate(
    component: CanComponentDeactivate
  ): boolean | Observable<boolean> {
    if (!component || typeof component.canDeactivate !== "function") {
      return true;
    }
    return component.canDeactivate();
  }
}
